/**
 * AIF-01 — Add / Edit class dialog (§11.2). Captures classIndex, className,
 * displayName (en), description (en), active, sortOrder.
 *
 * §3.1 / AC6 invariant: when the class being edited has `hasFindings: true`,
 * the Index field is DISABLED with an explanatory tooltip ("Index is locked —
 * findings reference this class…"). The server still 409s
 * (`class_index_immutable`) as a backstop; if it ever fires we surface it as
 * a clear error alert. Renaming the class at the same index is always allowed.
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import type {
  AiClassRole,
  AiFindingModelClass,
  CreateAiFindingModelClassBody,
  UpdateAiFindingModelClassBody,
} from "@aeriosiq/types";
import {
  useCreateAiModelClassMutation,
  useUpdateAiModelClassMutation,
} from "./aiModelQueries.js";
import { conflictMessage, errStatus } from "./apiError.js";

/** The §3.1 tooltip + the matching 409-backstop message (one source). */
export const INDEX_LOCKED_TOOLTIP =
  "Index is locked — findings reference this class. Re-train with changed indices ⇒ create a new model.";

/** AIARCH-01 — the v1 class-role options (label + value). The picker offers
 *  only the v1 values; an unknown future role read back from the server is
 *  shown verbatim (see the value fallback in the dialog). */
export const CLASS_ROLE_OPTIONS: readonly {
  value: AiClassRole;
  label: string;
}[] = [
  { value: "potential_defect", label: "Potential defect" },
  { value: "component", label: "Component (segmentation)" },
];

/** Friendly label for a class-role value; unknown future roles render verbatim. */
export function classRoleLabel(role: string): string {
  return CLASS_ROLE_OPTIONS.find((o) => o.value === role)?.label ?? role;
}

export interface ClassDialogProps {
  open: boolean;
  modelId: string;
  /** Edit mode when set; create mode otherwise. */
  klass?: AiFindingModelClass | null;
  /** Default index for create mode (= max existing + 1). */
  nextIndex: number;
  onClose: () => void;
  onSaved: () => void;
}

export function ClassDialog({
  open,
  modelId,
  klass,
  nextIndex,
  onClose,
  onSaved,
}: ClassDialogProps): ReactElement {
  const isEdit = klass != null;
  const indexLocked = isEdit && klass.hasFindings;

  const [classIndex, setClassIndex] = useState<string>(
    isEdit ? String(klass.classIndex) : String(nextIndex),
  );
  const [className, setClassName] = useState(klass?.className ?? "");
  const [displayName, setDisplayName] = useState(klass?.displayName?.en ?? "");
  const [description, setDescription] = useState(klass?.description?.en ?? "");
  const [isActive, setIsActive] = useState(klass?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState<string>(
    isEdit ? String(klass.sortOrder) : String(nextIndex),
  );
  // AIARCH-01 — per-class role. Default to potential_defect for new classes;
  // for an existing class, seed from the (server-default-guaranteed) value.
  const [classRole, setClassRole] = useState<string>(
    klass?.classRole ?? "potential_defect",
  );

  const create = useCreateAiModelClassMutation(modelId);
  const update = useUpdateAiModelClassMutation(modelId);
  const pending = create.isPending || update.isPending;

  const indexNum = Number(classIndex);
  const indexValid =
    classIndex.trim().length > 0 && Number.isInteger(indexNum) && indexNum >= 0;
  const nameValid =
    className.trim().length > 0 && className.trim().length <= 100;
  const canSubmit = indexValid && nameValid && !pending;

  function handleSubmit(): void {
    if (!canSubmit) return;
    const dn = displayName.trim();
    const desc = description.trim();
    const sortNum = Number(sortOrder);
    if (isEdit) {
      const body: UpdateAiFindingModelClassBody = {
        className: className.trim(),
        displayName: dn.length > 0 ? { en: dn } : null,
        description: desc.length > 0 ? { en: desc } : null,
        isActive,
        sortOrder: Number.isInteger(sortNum) ? sortNum : null,
        classRole: classRole as AiClassRole,
      };
      // Only send classIndex when it is editable AND actually changed —
      // never re-index a class with findings (the field is disabled, but
      // belt-and-braces so we don't trip the server 409 needlessly).
      if (!indexLocked && indexNum !== klass.classIndex) {
        body.classIndex = indexNum;
      }
      update.mutate(
        { classUuid: klass.classUuid, body },
        {
          onSuccess: () => {
            onSaved();
          },
        },
      );
    } else {
      const body: CreateAiFindingModelClassBody = {
        classIndex: indexNum,
        className: className.trim(),
        displayName: dn.length > 0 ? { en: dn } : null,
        description: desc.length > 0 ? { en: desc } : null,
        isActive,
        classRole: classRole as AiClassRole,
        ...(Number.isInteger(sortNum) ? { sortOrder: sortNum } : {}),
      };
      create.mutate(body, {
        onSuccess: () => {
          onSaved();
        },
      });
    }
  }

  const mut = isEdit ? update : create;
  const errorMsg = mut.isError
    ? errStatus(mut.error) === 409
      ? conflictMessage(mut.error, "Could not save the class.")
      : "Could not save the class."
    : null;

  const indexField = (
    <TextField
      label="Index"
      type="number"
      value={classIndex}
      onChange={(e) => {
        setClassIndex(e.target.value);
      }}
      fullWidth
      disabled={indexLocked}
      error={!indexValid}
      helperText={
        indexLocked
          ? INDEX_LOCKED_TOOLTIP
          : "0-based YOLO index — the contract with the .txt files."
      }
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit class" : "Add class"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
          {indexLocked ? (
            <Tooltip title={INDEX_LOCKED_TOOLTIP}>
              <span>{indexField}</span>
            </Tooltip>
          ) : (
            indexField
          )}
          <TextField
            label="Class name"
            value={className}
            onChange={(e) => {
              setClassName(e.target.value);
            }}
            fullWidth
            required
            slotProps={{ htmlInput: { maxLength: 100 } }}
            error={className.length > 0 && !nameValid}
          />
          <TextField
            label="Display name (en)"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
            }}
            fullWidth
          />
          <TextField
            label="Description (en)"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Sort order"
            type="number"
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value);
            }}
            fullWidth
          />
          <TextField
            select
            label="Class role"
            value={classRole}
            onChange={(e) => {
              setClassRole(e.target.value);
            }}
            fullWidth
            helperText="What a detection of this class means — routed to defects or to component segmentation."
          >
            {CLASS_ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
            {/* Tolerate an unknown future role read back from the server: show
                it as a selectable option so we never blank the field. */}
            {CLASS_ROLE_OPTIONS.every((o) => o.value !== classRole) ? (
              <MenuItem value={classRole}>{classRole}</MenuItem>
            ) : null}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => {
                  setIsActive(e.target.checked);
                }}
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isEdit ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
